import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  BriefcaseIcon, 
  ClockIcon, 
  CurrencyDollarIcon, 
  UsersIcon,
  ClipboardDocumentCheckIcon,
  BookOpenIcon,
  Cog6ToothIcon,
  DocumentTextIcon,
  AcademicCapIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ArrowPathIcon
} from '@heroicons/react/24/solid';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';
import StudentDashboard from './student/StudentDashboard';

const Dashboard = () => {
  const { user } = useAuth();
  const isAdmin = user && user.role === 'admin';
  // Mirrors backend BillingPermission — paralegals/clients/secretaries/interns can't see billing.
  const canSeeBilling = ['admin', 'owner', 'attorney', 'accountant'].includes(user?.role);

  const [dashboardData, setDashboardData] = useState({
    stats: [],
    totals: { deadlines: 0, events: 0, activeCases: 0, pendingTasks: 0 },
    upcomingDeadlines: [],
    upcomingEvents: [],
    activeCases: [],
    asOf: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchDashboardData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      const [statsRes, deadlines, events, cases] = await Promise.allSettled([
        api.get('/api/dashboard/stats/').then(res => res.data),
        api.get('/api/dashboard/deadlines/').then(res => res.data),
        api.get('/api/dashboard/events/').then(res => res.data),
        api.get('/api/dashboard/active-cases/').then(res => res.data)
      ]);

      // Support both old (array) and new (object with stats/totals) response formats
      const statsData = statsRes.status === 'fulfilled' ? statsRes.value : null;
      let stats = [];
      let totals = { deadlines: 0, events: 0, activeCases: 0, pendingTasks: 0 };
      let asOf = null;
      if (statsData) {
        if (Array.isArray(statsData)) {
          stats = statsData;
        } else {
          stats = statsData.stats || [];
          totals = statsData.totals || totals;
          asOf = statsData.asOf || null;
        }
      }

      const deadlinesArr = deadlines.status === 'fulfilled' ? deadlines.value || [] : [];
      const eventsArr = events.status === 'fulfilled' ? events.value || [] : [];
      const casesArr = cases.status === 'fulfilled' ? cases.value || [] : [];

      // Use API totals if available, else fall back to array lengths
      setDashboardData({
        stats,
        totals: {
          deadlines: totals.deadlines || deadlinesArr.length,
          events: totals.events || eventsArr.length,
          activeCases: totals.activeCases || casesArr.length,
          pendingTasks: totals.pendingTasks || 0,
        },
        upcomingDeadlines: deadlinesArr,
        upcomingEvents: eventsArr,
        activeCases: casesArr,
        asOf,
      });
      setError(null);
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const isStudent = user?.role === 'student';

  useEffect(() => {
    if (isStudent) return;
    fetchDashboardData();
  }, [fetchDashboardData, isStudent]);

  // Students get an entirely different (Academy) dashboard.
  if (isStudent) {
    return <StudentDashboard />;
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-10">
        <p className="text-red-500 text-lg">{error}</p>
        <button onClick={() => fetchDashboardData()} className="mt-4 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600">
          Retry
        </button>
      </div>
    );
  }

  // Map the stats to their icons, colors, and change indicators
  const statsWithIcons = dashboardData.stats.map(stat => {
    const icons = {
      'Active Cases': BriefcaseIcon,
      'Billable Hours': ClockIcon,
      'Monthly Revenue': CurrencyDollarIcon,
      'New Clients': UsersIcon
    };
    
    const colors = {
      'Active Cases': 'bg-blue-500',
      'Billable Hours': 'bg-violet-500',
      'Monthly Revenue': 'bg-amber-500',
      'New Clients': 'bg-green-500'
    };
    
    const change = stat.change || '0';
    const isPositive = change.startsWith('+') && change !== '+0';
    const isNegative = change.startsWith('-');

    return {
      ...stat,
      icon: icons[stat.name] || BriefcaseIcon,
      color: colors[stat.name] || 'bg-gray-500',
      change,
      isPositive,
      isNegative,
    };
  });

  const greeting = user?.first_name ? `Welcome back, ${user.first_name}` : 'Welcome back';

  return (
    <div>
      {/* Header with greeting and refresh */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{greeting}</h1>
          {dashboardData.asOf && (
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              Last updated: {new Date(dashboardData.asOf).toLocaleTimeString()}
            </p>
          )}
        </div>
        <button
          onClick={() => fetchDashboardData(true)}
          disabled={refreshing}
          className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          title="Refresh dashboard"
        >
          <ArrowPathIcon className={`h-4 w-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {/* Admin Quick Link - Only visible to admin users */}
      {isAdmin && (
        <div className="mb-6 bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 p-4 rounded">
          <div className="flex">
            <div className="flex-shrink-0">
              <Cog6ToothIcon className="h-5 w-5 text-amber-500" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-amber-800 dark:text-amber-300">
                Admin Access Detected
              </h3>
              <div className="mt-2 text-sm text-amber-700 dark:text-amber-200">
                <p>
                  You have administrator privileges. Access the Admin Dashboard to manage users, system settings, and view analytics.
                </p>
              </div>
              <div className="mt-3">
                <Link
                  to="/admin"
                  className="inline-flex items-center rounded-md bg-amber-50 dark:bg-amber-800 px-3 py-2 text-sm font-medium text-amber-800 dark:text-amber-100 shadow-sm ring-1 ring-inset ring-amber-600/20 hover:bg-amber-100 dark:hover:bg-amber-700"
                >
                  Go to Admin Dashboard
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {user?.role === 'paralegal' && (
        <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-4 rounded">
          <h3 className="text-sm font-medium text-blue-800 dark:text-blue-300">
            Paralegal workspace
          </h3>
          <p className="mt-1 text-sm text-blue-700 dark:text-blue-200">
            You can manage cases and clients assigned to you, upload documents, and use legal research.
            Billing, trust accounts, and admin settings are restricted to attorneys and administrators.
          </p>
        </div>
      )}

      {/* Quick action cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-700/10 overflow-hidden">
          <div className="p-5 bg-gradient-to-r from-blue-500 to-blue-600 text-white">
            <div className="flex items-center">
              <ClipboardDocumentCheckIcon className="h-8 w-8 mr-4" />
              <h3 className="text-xl font-semibold">Tasks & Deadlines</h3>
            </div>
            <div className="mt-1 flex items-baseline justify-center">
              <span className="text-2xl font-semibold text-white">
                {dashboardData.totals.deadlines}
              </span>
              <span className="ml-2 text-sm text-blue-100">upcoming deadlines</span>
            </div>
            {dashboardData.totals.pendingTasks > 0 && (
              <div className="mt-1 text-center text-xs text-blue-100">
                {dashboardData.totals.pendingTasks} pending tasks
              </div>
            )}
          </div>
          <div className="p-5">
            <Link to="/tasks" className="text-blue-600 dark:text-blue-400 hover:underline text-sm font-medium flex items-center justify-center">
              View All Tasks
            </Link>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-700/10 overflow-hidden">
          <div className="p-5 bg-gradient-to-r from-purple-500 to-purple-600 text-white">
            <div className="flex items-center">
              <BriefcaseIcon className="h-8 w-8 mr-4" />
              <h3 className="text-xl font-semibold">Active Cases</h3>
            </div>
            <div className="mt-1 flex items-baseline justify-center">
              <span className="text-2xl font-semibold text-white">
                {dashboardData.totals.activeCases}
              </span>
              <span className="ml-2 text-sm text-purple-100">cases in progress</span>
            </div>
          </div>
          <div className="p-5">
            <Link to="/cases" className="text-purple-600 dark:text-purple-400 hover:underline text-sm font-medium flex items-center justify-center">
              View All Cases
            </Link>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-700/10 overflow-hidden">
          <div className="p-5 bg-gradient-to-r from-amber-500 to-amber-600 text-white">
            <div className="flex items-center">
              <BookOpenIcon className="h-8 w-8 mr-4" />
              <h3 className="text-xl font-semibold">My Diary</h3>
            </div>
            <div className="mt-1 flex items-baseline justify-center">
              <span className="text-2xl font-semibold text-white">
                {dashboardData.totals.events}
              </span>
              <span className="ml-2 text-sm text-amber-100">upcoming events</span>
            </div>
          </div>
          <div className="p-5">
            <Link to="/diary" className="text-amber-600 dark:text-amber-400 hover:underline text-sm font-medium flex items-center justify-center">
              View Diary
            </Link>
          </div>
        </div>
      </div>

      {/* More quick actions */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Link to="/documents" className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow dark:shadow-gray-700/10 hover:shadow-md transition-shadow flex items-center">
          <DocumentTextIcon className="h-6 w-6 text-primary-500 mr-3" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Documents</span>
        </Link>
        
        {canSeeBilling && (
          <Link to="/billing" className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow dark:shadow-gray-700/10 hover:shadow-md transition-shadow flex items-center">
            <CurrencyDollarIcon className="h-6 w-6 text-primary-500 mr-3" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Billing</span>
          </Link>
        )}

        <Link to="/research" className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow dark:shadow-gray-700/10 hover:shadow-md transition-shadow flex items-center">
          <AcademicCapIcon className="h-6 w-6 text-primary-500 mr-3" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Research</span>
        </Link>

        {canSeeBilling && (
          <Link to="/analytics" className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow dark:shadow-gray-700/10 hover:shadow-md transition-shadow flex items-center">
            <ChartBarIcon className="h-6 w-6 text-primary-500 mr-3" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Analytics</span>
          </Link>
        )}
      </div>

      {/* Stats with trend indicators */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        {statsWithIcons.map((stat) => (
          <div key={stat.name} className="bg-white dark:bg-gray-800 overflow-hidden rounded-lg shadow dark:shadow-gray-700/10 transition-all duration-200 hover:shadow-md">
            <div className="p-5">
              <div className="flex items-center">
                <div className={`flex-shrink-0 rounded-md ${stat.color} p-3`}>
                  <stat.icon className="h-6 w-6 text-white" aria-hidden="true" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">{stat.name}</dt>
                    <dd className="flex items-baseline">
                      <div className="text-xl font-semibold text-gray-900 dark:text-white">{stat.value}</div>
                      {stat.change && stat.change !== '0' && (
                        <span className={`ml-2 inline-flex items-center text-xs font-medium ${
                          stat.isPositive 
                            ? 'text-green-600 dark:text-green-400' 
                            : stat.isNegative 
                              ? 'text-red-600 dark:text-red-400' 
                              : 'text-gray-500 dark:text-gray-400'
                        }`}>
                          {stat.isPositive && <ArrowTrendingUpIcon className="h-3 w-3 mr-0.5" />}
                          {stat.isNegative && <ArrowTrendingDownIcon className="h-3 w-3 mr-0.5" />}
                          {stat.change}%
                        </span>
                      )}
                    </dd>
                    <dd className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">vs last month</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Two column layout for deadlines and events */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* Upcoming Deadlines */}
        <div className="bg-white dark:bg-gray-800 shadow dark:shadow-gray-700/10 rounded-lg overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Upcoming Deadlines</h3>
          </div>
          <div className="p-6">
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {dashboardData.upcomingDeadlines.length === 0 ? (
                <li className="py-4 text-center text-gray-500 dark:text-gray-400">No upcoming deadlines</li>
              ) : (
                dashboardData.upcomingDeadlines.map((deadline) => (
                  <li key={deadline.id} className="py-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{deadline.title}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{deadline.case}</p>
                      </div>
                      <div className="flex flex-col items-end">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          deadline.priority === 'Urgent' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                          deadline.priority === 'High' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' :
                          'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                        }`}>
                          {deadline.priority}
                        </span>
                        <span className="mt-1 text-xs text-gray-500 dark:text-gray-400">{deadline.dueDate}</span>
                      </div>
                    </div>
                  </li>
                ))
              )}
            </ul>
          </div>
        </div>

        {/* Upcoming Events */}
        <div className="bg-white dark:bg-gray-800 shadow dark:shadow-gray-700/10 rounded-lg overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Upcoming Events</h3>
          </div>
          <div className="p-6">
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {dashboardData.upcomingEvents.length === 0 ? (
                <li className="py-4 text-center text-gray-500 dark:text-gray-400">No upcoming events</li>
              ) : (
                dashboardData.upcomingEvents.map((event) => (
                  <li key={event.id} className="py-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{event.title}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {event.client && <span>Client: {event.client}</span>}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {event.date}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {event.time}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{event.location}</p>
                      </div>
                    </div>
                  </li>
                ))
              )}
            </ul>
          </div>
        </div>
      </div>

      {/* Active Cases */}
      <div className="bg-white dark:bg-gray-800 shadow dark:shadow-gray-700/10 rounded-lg overflow-hidden mb-8">
        <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Active Cases</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Case</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Type</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Progress</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Last Action</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {dashboardData.activeCases.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">No active cases found</td>
                </tr>
              ) : (
                dashboardData.activeCases.map((caseItem) => (
                  <tr key={caseItem.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Link to={`/cases/${caseItem.id}`} className="text-primary-600 dark:text-primary-400 hover:underline">
                        {caseItem.title}
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">{caseItem.type}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                        {caseItem.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                        <div 
                          className={`h-2.5 rounded-full ${
                            caseItem.progress >= 75 ? 'bg-green-500' :
                            caseItem.progress >= 50 ? 'bg-blue-500' :
                            caseItem.progress >= 25 ? 'bg-amber-500' :
                            'bg-red-400'
                          }`}
                          style={{ width: `${caseItem.progress}%` }}
                        ></div>
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {caseItem.progress}%
                        {caseItem.totalTasks > 0 && (
                          <span className="ml-1">({caseItem.completedTasks}/{caseItem.totalTasks} tasks)</span>
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                      {caseItem.lastAction} <span className="text-xs">{caseItem.lastActionDate}</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
